import {
  bytesToBigInt,
  decimalToQuantityHex,
  hexToBytes,
  normalizeAddress,
  normalizeBytes32,
  stripHexPrefix,
} from "./bytes";
import type { ChainConfig } from "./types";

const TOP_UP_RELAY_FEE_SELECTOR = "0x14a2bcce";
const APPROVE_SELECTOR = "0x095ea7b3";
const ALLOWANCE_SELECTOR = "0xdd62ed3e";
const BALANCE_OF_SELECTOR = "0x70a08231";
const DECIMALS_SELECTOR = "0x313ce567";
const SYMBOL_SELECTOR = "0x95d89b41";
const RECEIPT_POLL_INTERVAL_MS = 1_500;
const RECEIPT_POLL_ATTEMPTS = 80;

interface TransactionReceipt {
  status?: string;
}

let activeProvider: EthereumProvider | null = null;

export function setEthereumProvider(provider: EthereumProvider | null): void {
  activeProvider = provider;
}

export function currentEthereumProvider(): EthereumProvider | null {
  return activeProvider;
}

export function ethereumProvider(): EthereumProvider {
  if (activeProvider === null) {
    throw new Error("No wallet is connected");
  }
  return activeProvider;
}

export async function connectWallet(): Promise<string> {
  const accounts = await ethereumProvider().request<string[]>({
    method: "eth_requestAccounts",
  });
  return firstWalletAccount(accounts);
}

export async function selectWalletAccount(): Promise<string> {
  const provider = ethereumProvider();
  try {
    await provider.request({
      method: "wallet_requestPermissions",
      params: [{ eth_accounts: {} }],
    });
  } catch (error) {
    if (!isUnsupportedMethodError(error)) {
      throw error;
    }
  }

  const accounts = await provider.request<string[]>({
    method: "eth_requestAccounts",
  });
  return firstWalletAccount(accounts);
}

function firstWalletAccount(accounts: string[]): string {
  const account = accounts[0];
  if (account === undefined) {
    throw new Error("No wallet account selected");
  }

  return normalizeAddress(account);
}

export async function walletChainId(): Promise<number> {
  const chainId = await ethereumProvider().request<string>({
    method: "eth_chainId",
  });
  return Number.parseInt(chainId, 16);
}

export async function switchNetwork(config: ChainConfig): Promise<void> {
  if (config.chain.type !== "evm") {
    throw new Error("Connected wallet does not support this chain");
  }
  const provider = ethereumProvider();
  const chainId = decimalToQuantityHex(BigInt(config.chain.chainId));
  await provider.request({
    method: "wallet_switchEthereumChain",
    params: [{ chainId }],
  });
}

export async function approveErc20(args: {
  from: string;
  tokenAddress: string;
  spender: string;
  amount: bigint;
}): Promise<string> {
  const data = `${APPROVE_SELECTOR}${encodeAddress(args.spender)}${encodeUint256(args.amount)}`;
  return sendTransaction({
    from: args.from,
    to: args.tokenAddress,
    data,
    value: 0n,
  });
}

export async function readErc20Allowance(args: {
  tokenAddress: string;
  owner: string;
  spender: string;
}): Promise<bigint> {
  const result = await ethCall(
    args.tokenAddress,
    `${ALLOWANCE_SELECTOR}${encodeAddress(args.owner)}${encodeAddress(args.spender)}`,
  );
  return BigInt(result);
}

export async function waitForTransactionSuccess(txHash: string, label: string): Promise<void> {
  for (let attempt = 0; attempt < RECEIPT_POLL_ATTEMPTS; attempt += 1) {
    const receipt = await ethereumProvider().request<TransactionReceipt | null>({
      method: "eth_getTransactionReceipt",
      params: [txHash],
    });
    if (receipt !== null) {
      if (receipt.status === "0x1") {
        return;
      }
      if (receipt.status === "0x0") {
        throw new Error(`${label} transaction failed`);
      }
      throw new Error(`${label} transaction returned an invalid receipt status`);
    }

    await delay(RECEIPT_POLL_INTERVAL_MS);
  }

  throw new Error(`${label} transaction was not confirmed in time`);
}

export async function topUpRelayFee(args: {
  from: string;
  contractAddress: string;
  cellId: string;
  amountWei: bigint;
}): Promise<string> {
  const data = `${TOP_UP_RELAY_FEE_SELECTOR}${encodeBytes32(args.cellId)}`;

  return sendTransaction({
    from: args.from,
    to: args.contractAddress,
    data,
    value: args.amountWei,
  });
}

export async function sendWalletAction(args: {
  from: string;
  action: { to: string; value: string; data: string };
}): Promise<string> {
  return sendTransaction({
    from: args.from,
    to: args.action.to,
    data: args.action.data,
    value: BigInt(args.action.value),
  });
}

export async function readErc20Decimals(tokenAddress: string): Promise<number> {
  const result = await ethCall(tokenAddress, DECIMALS_SELECTOR);
  const value = Number(BigInt(result));
  if (!Number.isInteger(value) || value < 0 || value > 255) {
    throw new Error("ERC-20 decimals value is invalid");
  }
  return value;
}

export async function readNativeBalance(account: string): Promise<bigint> {
  const balance = await ethereumProvider().request<string>({
    method: "eth_getBalance",
    params: [normalizeAddress(account), "latest"],
  });
  return BigInt(balance);
}

export async function readErc20Balance(tokenAddress: string, account: string): Promise<bigint> {
  const result = await ethCall(tokenAddress, `${BALANCE_OF_SELECTOR}${encodeAddress(account)}`);
  return BigInt(result);
}

export async function readErc20Symbol(tokenAddress: string): Promise<string | null> {
  try {
    const result = await ethCall(tokenAddress, SYMBOL_SELECTOR);
    const bytes = hexToBytes(result);
    if (bytes.length >= 96) {
      const offset = Number(bytesToBigInt(bytes.slice(0, 32)));
      const length = Number(bytesToBigInt(bytes.slice(offset, offset + 32)));
      const value = bytes.slice(offset + 32, offset + 32 + length);
      return new TextDecoder().decode(value);
    }
    return null;
  } catch {
    return null;
  }
}

export function parseUnits(value: string, decimals: number): bigint {
  const trimmed = value.trim();
  if (!/^\d+(\.\d+)?$/.test(trimmed)) {
    throw new Error("Amount must be a positive decimal number");
  }

  const [wholeRaw, fractionRaw = ""] = trimmed.split(".");
  if (wholeRaw === undefined) {
    throw new Error("Amount must be a positive decimal number");
  }
  if (fractionRaw.length > decimals) {
    throw new Error(`Amount has more than ${decimals} decimal places`);
  }

  const whole = BigInt(wholeRaw);
  const fraction = BigInt(fractionRaw.padEnd(decimals, "0") || "0");
  const scale = 10n ** BigInt(decimals);

  return whole * scale + fraction;
}

export function formatUnits(value: string | bigint, decimals: number): string {
  const amount = typeof value === "bigint" ? value : BigInt(value);
  const scale = 10n ** BigInt(decimals);
  const whole = amount / scale;
  const fraction = amount % scale;
  if (fraction === 0n) {
    return whole.toString();
  }
  const fractionText = fraction.toString().padStart(decimals, "0").replace(/0+$/, "");
  return `${whole}.${fractionText}`;
}

export function shortAddress(address: string): string {
  const normalized = normalizeAddress(address);
  return `${normalized.slice(0, 6)}...${normalized.slice(-4)}`;
}

async function sendTransaction(args: {
  from: string;
  to: string;
  data: string;
  value: bigint;
}): Promise<string> {
  return ethereumProvider().request<string>({
    method: "eth_sendTransaction",
    params: [
      {
        from: normalizeAddress(args.from),
        to: normalizeAddress(args.to),
        data: args.data,
        value: decimalToQuantityHex(args.value),
      },
    ],
  });
}

async function ethCall(to: string, data: string): Promise<string> {
  return ethereumProvider().request<string>({
    method: "eth_call",
    params: [
      {
        to: normalizeAddress(to),
        data,
      },
      "latest",
    ],
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function encodeBytes32(value: string): string {
  return stripHexPrefix(normalizeBytes32(value));
}

function encodeAddress(value: string): string {
  return stripHexPrefix(normalizeAddress(value)).padStart(64, "0");
}

function encodeUint256(value: bigint): string {
  if (value < 0n) {
    throw new Error("Cannot encode negative uint256");
  }
  return value.toString(16).padStart(64, "0");
}

function isUnsupportedMethodError(error: unknown): boolean {
  const candidate = error as { code?: unknown; message?: unknown };
  if (candidate.code === 4200 || candidate.code === -32601) {
    return true;
  }
  return (
    typeof candidate.message === "string" &&
    /unsupported|not supported|method not found|does not exist/i.test(candidate.message)
  );
}
